import { FC, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useGetActivityUsageQuery } from '../api/api';

type ActivityPurposeType = 'paid' | 'trial';

const ACTIVITY_TABS: Array<{
  key: ActivityPurposeType;
  label: string;
  description: string;
  rules: string[];
}> = [
  {
    key: 'paid',
    label: 'Paid',
    description: 'Данные рассчитываются по тем же правилам, по которым сессии списываются в тарифе.',
    rules: [
      'Учитываются только сессии организаций с активной платной подпиской.',
      'Сессия попадает в таблицу, если в ней есть хотя бы две реплики.',
      'Сессии, запущенные через кнопку Preview, не учитываются.',
    ],
  },
  {
    key: 'trial',
    label: 'Trial',
    description: 'В таблице показана вся активность пользователей организаций с активным триальным периодом.',
    rules: [
      'Сессия попадает в таблицу, если в ней есть хотя бы две реплики.',
      'Учитываются в том числе сессии, запущенные через кнопку Preview.',
      'Учитываются в т.ч. сессии, проигранные авторами программ.',
    ],
  },
];

const isPurposeType = (value: string | undefined): value is ActivityPurposeType =>
  value === 'paid' || value === 'trial';

const formatDateLabel = (dateKey: string): string => {
  const date = new Date(`${dateKey}T00:00:00`);
  return new Intl.DateTimeFormat('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  }).format(date);
};

export const Activity: FC = () => {
  const { purposeType: purposeTypeParam } = useParams<{ purposeType: string }>();
  const purposeType: ActivityPurposeType = isPurposeType(purposeTypeParam) ? purposeTypeParam : 'paid';
  const { data, isLoading } = useGetActivityUsageQuery(purposeType);

  const activeTab = ACTIVITY_TABS.find((tab) => tab.key === purposeType) ?? ACTIVITY_TABS[0];
  const totalsByDate = useMemo(() => {
    const totals: Record<string, number> = {};

    data?.dates.forEach((dateKey) => {
      totals[dateKey] = data.organizations.reduce(
        (sum, organization) => sum + (organization.sessions_by_date[dateKey] ?? 0),
        0,
      );
    });

    return totals;
  }, [data]);
  const grandTotal = useMemo(
    () => data?.organizations.reduce((sum, organization) => sum + organization.total_sessions, 0) ?? 0,
    [data],
  );

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-semibold">Activity</h1>
        </div>
      </div>

      <div className="mb-6 border-b border-gray-200">
        <div className="flex gap-2">
          {ACTIVITY_TABS.map((tab) => {
            const isActive = tab.key === activeTab.key;

            return (
              <Link
                key={tab.key}
                to={`/activity/${tab.key}`}
                className={`rounded-t-lg border border-b-0 px-4 py-2 text-sm font-semibold ${
                  isActive
                    ? 'border-blue-200 bg-blue-50 text-blue-700'
                    : 'border-transparent bg-transparent text-gray-600 hover:bg-gray-50'
                }`}
              >
                {tab.label}
              </Link>
            );
          })}
        </div>
      </div>

      <div className="mb-6 rounded-lg border border-blue-100 bg-blue-50 px-5 py-4">
        <h2 className="text-sm font-semibold text-blue-900">Как рассчитываются данные</h2>
        <p className="mt-2 text-sm text-blue-900">{activeTab.description}</p>
        <ul className="mt-3 list-disc space-y-1 pl-5 text-sm text-blue-900">
          {activeTab.rules.map((rule) => (
            <li key={rule}>{rule}</li>
          ))}
        </ul>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="sticky left-0 z-10 bg-gray-50 px-6 py-3 text-left text-xs font-semibold tracking-wide text-gray-600 uppercase">
                  Organization
                </th>
                {data?.dates.map((dateKey) => (
                  <th
                    key={dateKey}
                    className="px-4 py-3 text-center text-xs font-semibold tracking-wide text-gray-600 uppercase"
                  >
                    {formatDateLabel(dateKey)}
                  </th>
                ))}
                <th className="px-4 py-3 text-center text-xs font-semibold tracking-wide text-gray-600 uppercase">
                  Total
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {data?.organizations.length ? (
                <>
                  {data.organizations.map((organization) => (
                    <tr key={organization.organization_id} className="hover:bg-gray-50">
                      <td className="sticky left-0 bg-white px-6 py-4 text-sm font-medium whitespace-nowrap text-gray-900">
                        {organization.organization_name}
                      </td>
                      {data.dates.map((dateKey) => {
                        const value = organization.sessions_by_date[dateKey] ?? 0;
                        const hasUsage = value > 0;

                        return (
                          <td
                            key={`${organization.organization_id}:${dateKey}`}
                            className={`px-4 py-4 text-center text-sm whitespace-nowrap ${
                              hasUsage ? 'font-semibold text-blue-700' : 'text-gray-500'
                            }`}
                          >
                            <span
                              className={`inline-flex min-w-10 justify-center rounded-md px-2 py-1 ${
                                hasUsage ? 'bg-blue-50' : 'bg-gray-50'
                              }`}
                            >
                              {value}
                            </span>
                          </td>
                        );
                      })}
                      <td className="px-4 py-4 text-center text-sm font-semibold whitespace-nowrap text-gray-900">
                        <span className="inline-flex min-w-12 justify-center rounded-md bg-gray-100 px-2 py-1">
                          {organization.total_sessions}
                        </span>
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-gray-50">
                    <td className="sticky left-0 bg-gray-50 px-6 py-4 text-sm font-semibold whitespace-nowrap text-gray-900">
                      Total
                    </td>
                    {data.dates.map((dateKey) => (
                      <td
                        key={`total:${dateKey}`}
                        className="px-4 py-4 text-center text-sm font-semibold whitespace-nowrap text-gray-900"
                      >
                        <span className="inline-flex min-w-12 justify-center rounded-md bg-blue-100 px-2 py-1 text-blue-800">
                          {totalsByDate[dateKey] ?? 0}
                        </span>
                      </td>
                    ))}
                    <td className="px-4 py-4 text-center text-sm font-semibold whitespace-nowrap text-gray-900">
                      <span className="inline-flex min-w-12 justify-center rounded-md bg-blue-600 px-2 py-1 text-white">
                        {grandTotal}
                      </span>
                    </td>
                  </tr>
                </>
              ) : (
                <tr>
                  <td
                    colSpan={(data?.dates.length ?? 0) + 2}
                    className="px-6 py-10 text-center text-sm text-gray-500"
                  >
                    No organizations with an active {purposeType} subscription were found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
